import { SyntheticEvent, useEffect, useRef } from 'react';
import Highlighter from 'react-highlight-words';
import { useParams } from 'react-router-dom';

import { Button, InputNumber, Typography, message } from 'antd';
import cn from 'classnames';
import { filter, find, isEmpty, map } from 'lodash';
import { observer } from 'mobx-react';
import { normalizeOrderProduct } from 'types/Orders/products';
import { normalizeOrderService } from 'types/Orders/services';
import { TPicture } from 'types/picture';
import { TUser } from 'types/user/user';

import AutoResizeText from '@atoms/listsCard/autoResizeText/autoResizeText';
import useGetCardWidth from '@atoms/listsCard/hooks/useGetCardWidth';
import { Common } from '@classes/commonMethods';
import { SettingsManager } from '@classes/settingsManager';
import RenderIf from '@common/RenderIf';
import {
    faCheck,
    faClock,
    faEye,
    faPencil,
    faPlus,
    faRubleSign,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { columnViewStore } from '@molecules/listViewChanger/listViewStore';
import { TUserExtended } from '@organisms/workers/common/types';
import { CardQuantityStore } from '@store/cardQuantityStore';
import { rootStore } from '@store/rootStore/instanse';
import { imagePicker } from '@utils/imagePicker';
import { useLocalStore } from '@utils/useLocalStore';

import styles from './entityListCard.module.scss';

const { Text } = Typography;

export type TEntityListCardKind = 'product' | 'service' | 'task';

type TPermissionEntry = { show?: boolean; update?: boolean };
type TAvailableItemsKey = 'available_products' | 'available_services' | 'available_tasks';

// Намеренно широкий, "read-only" тип: реальные значения, которые сюда попадают —
// это TProduct/TService/TTask из вызывающего кода, здесь читаются только общие
// для всех трёх сущностей поля плюс те, что нужны конкретной ветке по `kind`.
export type TEntityListCardData = {
    id: number;
    name: string;
    picture?: TPicture;
    pictures?: TPicture[];
    price?: number;
    reward?: number;
    artnumber?: string;
    duration?: number;
    quantity?: number;
    category?: { id: number };
    permissions?: TPermissionEntry[];
    permissions_for_worker?: TPermissionEntry[];
};

// Входящий список уже выбранных элементов — только id обязателен (задачи всегда
// приходят как {id}[], без name/parentId).
export type TEntityListCardSelectListItem = {
    id: number;
    name?: string;
    parentId?: number | null;
};

// Наружу в onSelect всегда отдаём полностью заполненный объект.
export type TEntityListCardSelectedEntity = {
    id: number;
    name: string;
    parentId: number | null;
};

type Props = {
    kind: TEntityListCardKind;
    data: TEntityListCardData;
    selectElemList: TEntityListCardSelectListItem[];
    openCard: (id: number, data?: any) => void | null;
    onSelect: (e: SyntheticEvent, entity: TEntityListCardSelectedEntity) => void | null;
    avoidRootStore?: boolean;
    noQuantitySelector?: boolean;
    noNavigation?: boolean;
    hideQuantity?: boolean;
    searchQuery?: string;
    showPermisionButtons?: boolean;
    onPermissionButtonClick?: (id: number, type: 'show' | 'update' | 'execute') => void;
    user?: TUserExtended;
    setUser?: React.Dispatch<React.SetStateAction<TUser>>;
    isResource?: boolean;
    isPermissions?: boolean;
};

const getAvailableItemsKey = (kind: TEntityListCardKind): TAvailableItemsKey => {
    if (kind === 'product') return 'available_products';
    if (kind === 'service') return 'available_services';
    return 'available_tasks';
};

const getInitialQuantity = (kind: TEntityListCardKind, entityId: number): number => {
    if (kind === 'product') return rootStore.orderStore.products[entityId]?.quantity ?? 0;
    if (kind === 'service') return rootStore.orderStore.services[entityId]?.quantity ?? 0;
    return 0;
};

const getMaxQuantity = (kind: TEntityListCardKind, data: TEntityListCardData): number | undefined =>
    kind === 'product' ? data.quantity : undefined;

const getIsCurrentUser = (
    kind: TEntityListCardKind,
    params: Readonly<Record<string, string | undefined>>,
    currentUserId: number
): boolean => {
    const relevantParamId = kind === 'product' ? params?.userId : params?.workerPositionId;
    return +relevantParamId === currentUserId;
};

// У task нет ни одного из этих обходов (showPermisionButtons/isPermissions/isResource) —
// открытие всегда проверяет права напрямую.
const getShouldCheckPermission = (
    kind: TEntityListCardKind,
    isPermissions: boolean,
    showPermisionButtons: boolean,
    isResource: boolean
): boolean => {
    if (kind === 'task') return true;

    const showButtonsBypass = showPermisionButtons;
    const permissionsBypass = !isPermissions;
    const resourceBypass = kind === 'service' && isResource;

    return !(showButtonsBypass || permissionsBypass || resourceBypass);
};

const EntityListCard: React.FC<Props> = observer(
    ({
        kind,
        data,
        selectElemList,
        openCard,
        onSelect,
        avoidRootStore = false,
        noQuantitySelector = false,
        noNavigation = false,
        hideQuantity,
        searchQuery = '',
        showPermisionButtons = false,
        onPermissionButtonClick,
        user,
        setUser,
        isResource = false,
        isPermissions = true,
    }) => {
        const isProduct = kind === 'product';
        const isTask = kind === 'task';
        const entityId = data.id;
        const isSelected = selectElemList.some((sli) => sli.id === entityId);

        const cardRef = useRef(null);
        const cardWidth = useGetCardWidth({ cardRef });
        const params = useParams();
        const creds = SettingsManager.getConnectionCredentials();
        const isCurrentUser = getIsCurrentUser(kind, params, creds.userId);
        const availableItemsKey = getAvailableItemsKey(kind);
        const availableItemIds = user?.worker_positions?.[0]?.[availableItemsKey];

        // Квантити-стор нужен только product/service, но хук вызываем безусловно
        // (правило хуков) — для task это дешёвый, никогда не читаемый инстанс.
        const store = useLocalStore(
            () =>
                new CardQuantityStore(
                    entityId,
                    getInitialQuantity(kind, entityId),
                    getMaxQuantity(kind, data)
                )
        );

        const updateOrderStoreQuantity = (quantity: number) => {
            if (isProduct) {
                rootStore.orderStore.updateProductList(normalizeOrderProduct(data as any, quantity));
            } else {
                rootStore.orderStore.updateServicesList(normalizeOrderService(data as any, quantity));
            }
        };

        const handleAddItem = () => {
            setUser?.((prev) => ({
                ...prev,
                worker_positions: map(prev.worker_positions, (position, index) =>
                    index === 0
                        ? {
                              ...position,
                              [availableItemsKey]: find(
                                  position[availableItemsKey],
                                  (id) => entityId === id
                              )
                                  ? filter(position[availableItemsKey], (id) => id !== entityId)
                                  : [...position[availableItemsKey], entityId],
                          }
                        : position
                ),
            }));
        };

        const notifySelect = (e: SyntheticEvent) =>
            onSelect(e, { id: entityId, name: data.name, parentId: data.category?.id ?? null });

        const handleSelectCard = (e: SyntheticEvent) => {
            notifySelect(e);

            if (isTask || avoidRootStore) return;

            if (store.controlsIsVisible) {
                store.setQuantity(0);
                store.setVisible(false);
            } else {
                store.setVisible(true);
                store.inc();
            }
            updateOrderStoreQuantity(store.quantity);
        };

        const handlePlusClick = (e: SyntheticEvent) => {
            e.stopPropagation();
            store.inc();
            updateOrderStoreQuantity(store.quantity);
        };

        const handleMinusClick = (e: SyntheticEvent) => {
            e.stopPropagation();
            store.dec();
            updateOrderStoreQuantity(store.quantity);
            if (!store.controlsIsVisible) {
                notifySelect(e);
            }
        };

        const handleOpenCard = () => {
            const shouldCheckPermission = getShouldCheckPermission(
                kind,
                isPermissions,
                showPermisionButtons,
                isResource
            );

            if (shouldCheckPermission && !data.permissions?.[0]?.show) {
                message.error('У вас нет прав на выполнение данной операции');
                return;
            }
            openCard(entityId, data);
        };

        useEffect(() => {
            if (rootStore.orderStore.allEntitiesCount == 0) {
                store.setQuantity(0);
                store.setVisible(false);
            }
        }, [rootStore.orderStore.allEntitiesCount]);

        const normalizedPictures = map(data?.pictures, (pic) => imagePicker({ picture: pic }));
        const normalizedPicture = imagePicker({ picture: data?.picture });
        const avatar = normalizedPictures?.[0] || normalizedPicture;

        const renderImage = () => (
            <div
                className={cn(
                    styles['card__image-container'],
                    isProduct
                        ? styles['card__image-container_contain']
                        : styles['card__image-container_cover'],
                    { [styles['card__image-container_vertical']]: columnViewStore.col > 1 }
                )}
            >
                <img src={avatar} />
                <RenderIf condition={isProduct && !hideQuantity}>
                    <span className={styles['card__quantity']}>{data.quantity}</span>
                </RenderIf>
            </div>
        );

        const renderFooterLeft = () =>
            isProduct ? (
                <div className={styles['card__articul']}>
                    {'Арт. '}
                    <Typography.Text copyable={{ tooltips: ['копировать', 'скопировано'] }}>
                        {data.artnumber}
                    </Typography.Text>
                </div>
            ) : (
                <div className={styles['card__duration']}>
                    <FontAwesomeIcon icon={faClock} />
                    <Text>{Common.convertMinutes2Date((data.duration ?? 0) / 60)}</Text>
                </div>
            );

        const renderInfo = () => (
            <div className={styles['card__info']}>
                <p
                    className={cn(styles['card__name'], {
                        [styles['card__name-select-margin']]: !isProduct && columnViewStore.col == 1,
                    })}
                >
                    <Highlighter
                        className="ant-typography"
                        searchWords={searchQuery.split(' ') || []}
                        autoEscape={true}
                        textToHighlight={data.name}
                    />
                </p>
                <div className={styles['card__footer-row']}>
                    {renderFooterLeft()}
                    <div className={styles['card__price-container']}>
                        <AutoResizeText maxWidth={cardWidth}>
                            {Common.formatCurrency(isTask ? data.reward : data.price)}&nbsp;
                            <FontAwesomeIcon color="black" icon={faRubleSign} />
                        </AutoResizeText>
                    </div>
                </div>
            </div>
        );

        const renderTaskSelectButton = () => (
            <RenderIf condition={data.permissions?.[0]?.show}>
                <div className={styles['card__select-btn']}>
                    <Button
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCard(e);
                        }}
                        shape="circle"
                        type={isSelected ? 'primary' : 'default'}
                    />
                </div>
            </RenderIf>
        );

        const renderQuantityControls = () => (
            <div
                className={
                    styles[columnViewStore.col > 1 ? 'card__controls-vertical' : 'card__controls']
                }
            >
                <div className={styles['card__select-row']}>
                    <InputNumber
                        className={styles['card__select-amount']}
                        controls={false}
                        value={store.quantity}
                        min={0}
                        max={isProduct ? data.quantity : undefined}
                        onClick={(e) => e.stopPropagation()}
                        style={{ width: '100%' }}
                    />
                    <Button
                        className={styles['card__select-rect-btn']}
                        onClick={handleSelectCard}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        shape="default"
                        type={isSelected ? 'primary' : 'default'}
                    />
                </div>
                <div className={styles['card__select-row']}>
                    <Button
                        className={styles['card__select-minus']}
                        onClick={handleMinusClick}
                        type="primary"
                    >
                        -
                    </Button>
                    <Button
                        className={styles['card__select-plus']}
                        onClick={handlePlusClick}
                        type="primary"
                    >
                        +
                    </Button>
                </div>
            </div>
        );

        const renderSelectToggleOrQuantity = () => (
            <RenderIf
                condition={!noQuantitySelector && !showPermisionButtons && data.permissions?.[0]?.show}
            >
                {!isSelected ? (
                    <Button
                        className={styles['card__controls-select-btn']}
                        onClick={handleSelectCard}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        shape="circle"
                        type={isSelected ? 'primary' : 'default'}
                    />
                ) : (
                    renderQuantityControls()
                )}
            </RenderIf>
        );

        const renderPermissionButtonsRow = () => (
            <RenderIf condition={showPermisionButtons}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <RenderIf condition={isEmpty(selectElemList)}>
                        <Button
                            shape="circle"
                            icon={<FontAwesomeIcon icon={faEye} />}
                            onClick={() => onPermissionButtonClick?.(entityId, 'show')}
                            type={
                                (isCurrentUser ? data.permissions : data.permissions_for_worker)?.[0]
                                    ?.show
                                    ? 'primary'
                                    : 'default'
                            }
                        />
                        <Button
                            shape="circle"
                            icon={<FontAwesomeIcon icon={faPencil} />}
                            onClick={() => onPermissionButtonClick?.(entityId, 'update')}
                            type={
                                (isCurrentUser ? data.permissions : data.permissions_for_worker)?.[1]
                                    ?.update
                                    ? 'primary'
                                    : 'default'
                            }
                        />
                        <Button
                            shape="circle"
                            icon={<FontAwesomeIcon icon={faPlus} />}
                            type={!!find(availableItemIds, (id) => id === entityId) ? 'primary' : 'default'}
                            onClick={handleAddItem}
                        />
                    </RenderIf>
                    <Button
                        onClick={handleSelectCard}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        shape="circle"
                        type={isSelected ? 'primary' : 'default'}
                    />
                </div>
            </RenderIf>
        );

        const renderProductServiceSelectGroup = () => (
            <div className={styles['card__select-group']}>
                <RenderIf condition={noQuantitySelector}>
                    <Button
                        className={styles['card__controls-select-btn']}
                        onClick={handleSelectCard}
                        icon={<FontAwesomeIcon icon={faCheck} />}
                        shape="circle"
                        type={isSelected ? 'primary' : 'default'}
                    />
                </RenderIf>
                {renderSelectToggleOrQuantity()}
                {renderPermissionButtonsRow()}
            </div>
        );

        return (
            <div
                ref={cardRef}
                className={cn(styles.card, { [styles['card_vertical']]: columnViewStore.col > 1 })}
                onClick={(e: SyntheticEvent) => {
                    if (noNavigation) {
                        handleSelectCard(e);
                    } else {
                        handleOpenCard();
                    }
                }}
            >
                {renderImage()}
                {renderInfo()}
                {isTask ? renderTaskSelectButton() : renderProductServiceSelectGroup()}
            </div>
        );
    }
);

export { EntityListCard };
